import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../supabase/supabaseClient";
import { followOrg, isFollowingOrg, unfollowOrg } from "../../services/followsService";
import "./PublicOrgProfile.css";

function PublicOrgProfile() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [org, setOrg] = useState(null);
    const [authedUser, setAuthedUser] = useState(null);
    const [followingOrg, setFollowingOrg] = useState(false);
    const [followingLoading, setFollowingLoading] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [orgAvatarUrl, setOrgAvatarUrl] = useState("");
    const [orgAvatarFailed, setOrgAvatarFailed] = useState(false);
    const [members, setMembers] = useState([]);
    const [posts, setPosts] = useState([]);

    const stripOrgPostMarker = (body) => {
        if (typeof body !== "string") return "";
        return body.replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "").trimEnd();
    };

    useEffect(() => {
        let mounted = true;

        const loadOrgProfile = async () => {
            setLoading(true);
            setError("");

            try {
                const { data: orgData, error: orgError } = await supabase
                    .from("org_profiles")
                    .select("id, name, type, city, website, bio, verified, avatar_url")
                    .eq("id", id)
                    .maybeSingle();

                if (orgError || !orgData) {
                    if (mounted) {
                        setError("Organization profile not found.");
                        setOrg(null);
                    }
                    return;
                }

                if (mounted) setOrg(orgData);
                if (mounted) {
                    setOrgAvatarUrl(orgData.avatar_url || "");
                    setOrgAvatarFailed(false);
                }

                const { data: sessionData } = await supabase.auth.getSession();
                const currentUser = sessionData?.session?.user ?? null;
                if (mounted) {
                    setAuthedUser(currentUser);
                }

                if (currentUser && mounted) {
                    try {
                        const isFollowing = await isFollowingOrg(orgData.id);
                        setFollowingOrg(!!isFollowing);
                    } catch (followError) {
                        console.error("Error loading org follow status:", followError);
                        setFollowingOrg(false);
                    }
                } else if (mounted) {
                    setFollowingOrg(false);
                }

                const { data: membershipData } = await supabase
                    .from("org_memberships")
                    .select("user_id, orgrole")
                    .eq("org_id", id)
                    .limit(100);

                const memberRows = membershipData || [];
                const memberIds = memberRows.map((m) => m.user_id).filter(Boolean);

                let userNameMap = {};
                if (memberIds.length > 0) {
                    const { data: userProfiles } = await supabase
                        .from("user_profiles")
                        .select("user_id, first_name, last_name")
                        .in("user_id", memberIds);

                    (userProfiles || []).forEach((profile) => {
                        const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                        if (fullName) userNameMap[profile.user_id] = fullName;
                    });

                    const unresolvedIds = memberIds.filter((userId) => !userNameMap[userId]);
                    if (unresolvedIds.length > 0) {
                        const { data: playerProfiles } = await supabase
                            .from("player_profiles")
                            .select("user_id, firstname, lastname")
                            .in("user_id", unresolvedIds);

                        (playerProfiles || []).forEach((profile) => {
                            const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                            if (fullName) userNameMap[profile.user_id] = fullName;
                        });
                    }
                }

                if (mounted) {
                    setMembers(memberRows.map((m) => ({
                        userId: m.user_id,
                        role: m.orgrole || "member",
                        name: userNameMap[m.user_id] || "Member",
                    })));
                }

                const marker = `[posted_as_org:${id}]`;
                const { data: rawPosts } = await supabase
                    .from("posts")
                    .select("id, author_id, body, created_at, post_media(id, media_type, url, position)")
                    .ilike("body", `%${marker}%`)
                    .is("deleted_at", null)
                    .order("created_at", { ascending: false })
                    .limit(20);

                const postRows = rawPosts || [];
                const postAuthorIds = Array.from(new Set(postRows.map((p) => p.author_id).filter(Boolean)));
                let postAuthorMap = {};

                if (postAuthorIds.length > 0) {
                    const { data: up } = await supabase
                        .from("user_profiles")
                        .select("user_id, first_name, last_name")
                        .in("user_id", postAuthorIds);

                    (up || []).forEach((profile) => {
                        const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                        if (fullName) postAuthorMap[profile.user_id] = fullName;
                    });

                    const unresolved = postAuthorIds.filter((uid) => !postAuthorMap[uid]);
                    if (unresolved.length > 0) {
                        const { data: pp } = await supabase
                            .from("player_profiles")
                            .select("user_id, firstname, lastname")
                            .in("user_id", unresolved);

                        (pp || []).forEach((profile) => {
                            const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                            if (fullName) postAuthorMap[profile.user_id] = fullName;
                        });
                    }
                }

                if (mounted) {
                    setPosts(postRows.map((post) => ({
                        id: post.id,
                        authorId: post.author_id,
                        authorName: postAuthorMap[post.author_id] || "Member",
                        body: stripOrgPostMarker(post.body || ""),
                        createdAt: post.created_at,
                        media: Array.isArray(post.post_media)
                            ? [...post.post_media]
                                .sort((a, b) => (a.position || 0) - (b.position || 0))
                                .map((item) => ({ type: item.media_type, url: item.url }))
                            : [],
                    })));
                }
            } catch (loadError) {
                console.error("Error loading public org profile:", loadError);
                if (mounted) setError("Could not load organization profile right now.");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadOrgProfile();

        return () => {
            mounted = false;
        };
    }, [id]);

    const sortedMembers = useMemo(() => {
        const priority = { admin: 1, coach: 2, recruiter: 3, scout: 4 };
        return [...members].sort((a, b) => {
            const aRole = (a.role || "").toLowerCase();
            const bRole = (b.role || "").toLowerCase();
            const aRank = priority[aRole] || 99;
            const bRank = priority[bRole] || 99;
            if (aRank !== bRank) return aRank - bRank;
            return a.name.localeCompare(b.name);
        });
    }, [members]);

    if (loading) {
        return (
            <main className="page">
                <div className="public-org-shell">
                    <section className="public-org-hero public-org-skeleton" />
                    <section className="public-org-card public-org-skeleton" />
                    <section className="public-org-card public-org-skeleton" />
                </div>
            </main>
        );
    }

    if (error || !org) {
        return (
            <main className="page">
                <div className="public-org-shell">
                    {/* <Link to="/explore" className="public-org-back-link">← Back to Explore</Link> */}
                    <section className="public-org-card">
                        <p>{error || "Organization profile not found."}</p>
                    </section>
                </div>
            </main>
        );
    }

    return (
        <main className="page">
            <div className="public-org-container">
                {/* <Link to="/explore" className="back-link">← Back to Explore</Link> */}

                <section className="profile-hero public-org-profile-hero">
                    <div className="hero-content">
                        {orgAvatarUrl && !orgAvatarFailed ? (
                            <img
                                src={orgAvatarUrl}
                                alt={org.name}
                                className="avatar-circle avatar-img public-org-avatar-circle"
                                onError={() => setOrgAvatarFailed(true)}
                            />
                        ) : (
                            <div className="avatar-circle public-org-avatar-circle">{(org.name || "O").slice(0, 1).toUpperCase()}</div>
                        )}
                        <div className="hero-text">
                            <h1 className="profile-name">
                                {org.name}
                                {org.verified && <span className="verified-badge">✓</span>}
                            </h1>
                            <p className="profile-subtitle">{org.type || "Organization"} Profile</p>
                            <div className="hero-meta">
                                {org.type && <span className="meta-pill">{org.type}</span>}
                                {org.city && <span className="meta-pill">City: {org.city}</span>}
                                <span className="meta-pill">Members: {sortedMembers.length}</span>
                                {org.website && (
                                    <a href={org.website} target="_blank" rel="noopener noreferrer" className="meta-pill meta-pill-link">
                                        Website
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                    {authedUser && (
                        <div className="public-org-hero-actions-row">
                            <div className="public-org-hero-actions">
                                <button
                                    className="btn-secondary"
                                    type="button"
                                    onClick={() => window.alert("Messaging organizations is coming soon.")}
                                >
                                    Message
                                </button>

                                <div className="public-org-more-wrap">
                                    <button
                                        className="btn-more"
                                        type="button"
                                        onClick={() => setMoreMenuOpen((prev) => !prev)}
                                        aria-expanded={moreMenuOpen}
                                    >
                                        More
                                    </button>
                                    {moreMenuOpen && (
                                        <div className="public-org-more-dropdown">
                                            <button
                                                className="public-org-more-item"
                                                type="button"
                                                onClick={async () => {
                                                    if (!org?.id || followingLoading) return;
                                                    setFollowingLoading(true);
                                                    try {
                                                        if (followingOrg) {
                                                            await unfollowOrg(org.id);
                                                            setFollowingOrg(false);
                                                        } else {
                                                            await followOrg(org.id);
                                                            setFollowingOrg(true);
                                                        }
                                                        setMoreMenuOpen(false);
                                                    } catch (followError) {
                                                        console.error("Error toggling org follow:", followError);
                                                    } finally {
                                                        setFollowingLoading(false);
                                                    }
                                                }}
                                                disabled={followingLoading}
                                            >
                                                {followingLoading ? "Working..." : followingOrg ? "Unfollow" : "Follow"}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                <div className="profile-sections">
                    <section className="profile-card">
                        <h2>About</h2>
                        <p className="about-text">{org.bio || "No organization bio yet."}</p>
                    </section>

                    <section className="profile-card profile-activity-section">
                        <h2>Activity</h2>
                        {posts.length === 0 ? (
                            <div className="activity-empty">
                                <p>No posts published by this organization yet.</p>
                            </div>
                        ) : (
                            <div className="activity-showcase">
                                <div className="activity-scroll">
                                    {posts.map((post) => (
                                        <div
                                            key={post.id}
                                            className="activity-post-card"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => navigate("/home", {
                                                state: {
                                                    focusPostId: post.id,
                                                    focusPostPreview: {
                                                        id: post.id,
                                                        author: org.name,
                                                        authorId: post.authorId,
                                                        orgId: org.id,
                                                        body: post.body,
                                                        media: post.media,
                                                        created_at: post.createdAt,
                                                        authorHeadline: `${org.type || "Organization"}${org.city ? ` · ${org.city}` : ""}`,
                                                    }
                                                }
                                            })}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    navigate("/home", {
                                                        state: {
                                                            focusPostId: post.id,
                                                            focusPostPreview: {
                                                                id: post.id,
                                                                author: org.name,
                                                                authorId: post.authorId,
                                                                orgId: org.id,
                                                                body: post.body,
                                                                media: post.media,
                                                                created_at: post.createdAt,
                                                                authorHeadline: `${org.type || "Organization"}${org.city ? ` · ${org.city}` : ""}`,
                                                            }
                                                        }
                                                    });
                                                }
                                            }}
                                        >
                                            {post.media.length > 0 && (
                                                <div className="activity-post-media">
                                                    {post.media[0].type === "video" ? (
                                                        <video src={post.media[0].url} controls preload="metadata" />
                                                    ) : (
                                                        <img src={post.media[0].url} alt="Organization post media" />
                                                    )}
                                                </div>
                                            )}
                                            <div className="activity-post-body">
                                                <p>{post.body.length > 120 ? `${post.body.slice(0, 120)}...` : post.body || "(No text content)"}</p>
                                                <div className="activity-post-date">
                                                    {new Date(post.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                                </div>
                                                <div className="public-org-post-credit">
                                                    Posted by <Link to={`/player/${post.authorId}`} className="public-org-inline-link">{post.authorName}</Link>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>

                    <section className="profile-card">
                        <h2>Team Members</h2>
                        {sortedMembers.length === 0 ? (
                            <p className="public-org-muted">No members visible yet.</p>
                        ) : (
                            <div className="public-org-members-grid">
                                {sortedMembers.map((member) => (
                                    <div key={member.userId} className="public-org-member-card">
                                        <Link to={`/player/${member.userId}`} className="public-org-member-name">
                                            {member.name}
                                        </Link>
                                        <span className="public-org-member-role">{member.role}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <section className="profile-card">
                        <h2>Details</h2>
                        <div className="public-org-details-grid">
                            <div className="public-org-detail-item">
                                <span className="public-org-detail-label">Organization Type</span>
                                <span className="public-org-detail-value">{org.type || "Organization"}</span>
                            </div>
                            <div className="public-org-detail-item">
                                <span className="public-org-detail-label">City</span>
                                <span className="public-org-detail-value">{org.city || "—"}</span>
                            </div>
                            <div className="public-org-detail-item">
                                <span className="public-org-detail-label">Website</span>
                                <span className="public-org-detail-value">
                                    {org.website ? (
                                        <a href={org.website} target="_blank" rel="noopener noreferrer" className="public-org-inline-link">
                                            {org.website}
                                        </a>
                                    ) : "—"}
                                </span>
                            </div>
                            <div className="public-org-detail-item">
                                <span className="public-org-detail-label">Verified</span>
                                <span className="public-org-detail-value">{org.verified ? "Yes" : "No"}</span>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </main>
    );
}

export default PublicOrgProfile;
